import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import back_app from '../../lib/back_app'
import Org_select from '../../components/auth/org_select'
import { useCatIds, cat_id2lbl } from '../../lib/cat'

let phone_rx = /^(\+\d{1,2}\s?)?1?-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/u

let field_cls = 'w-full px-4 py-3 rounded-xl border border-gray-300 '
  + 'focus:outline-none focus:ring-2 focus:ring-[#0066FF] text-gray-900'
let label_cls = 'block text-sm font-semibold text-gray-900 mb-1'
let types = ['creator', 'brand'] as const

export default function Onboarding() {
  let { logout } = useAuth0()
  let { ids: cat_ids } = useCatIds()
  let [step, step_set] = useState(0)
  let [user_type, user_type_set] = useState<'creator' | 'brand' | null>(null)
  let [first, first_set] = useState('')
  let [last, last_set] = useState('')
  let [nick, nick_set] = useState('')
  let [company, company_set] = useState('')
  let [website, website_set] = useState('')
  let [category, category_set] = useState('')
  let [phone, phone_set] = useState('')
  let [agree, agree_set] = useState(false)
  let [submitting, submitting_set] = useState(false)
  let [err, err_set] = useState<string | null>(null)

  let is_brand = user_type == 'brand'
  let is_creator = user_type == 'creator'

  let validate = ()=>{
    if (!first.trim())
      return 'Please input your first name'
    if (!last.trim())
      return 'Please input your last/family name'
    if (is_creator && !nick.trim())
      return 'Please input your display name / nickname'
    if (is_brand && !company.trim())
      return 'Please input your company name'
    if (is_brand && !website.trim())
      return 'Please input your company website'
    if (is_brand && !category)
      return 'Please select your brand category'
    if (!phone.trim() || !phone_rx.test(phone.trim()))
      return 'Please input a valid phone number. Example +18777755232'
    if (!agree)
      return 'Please accept the privacy policy, terms of service and disclaimer'
    return null
  }

  let on_submit = async ()=>{
    let msg = validate()
    if (msg) {
      err_set(msg)
      return
    }
    submitting_set(true)
    err_set(null)
    
    let must = async (p: Promise<any>)=>{
      let res = await p
      if (res?.err)
        throw new Error(res.err)
      return res
    }
    try {
      await must(back_app.user_set('first', first.trim()))
      await must(back_app.user_set('last', last.trim()))
      if (is_creator)
        await must(back_app.user_set('nick', nick.trim()))
      if (company.trim())
        await must(back_app.user_set('company', company.trim()))
      await must(back_app.user_set('phone', phone.trim()))
      if (website.trim())
        await must(back_app.org_set('website', website.trim()))
      if (is_brand && category)
        await must(back_app.org_set('cat', category))
      
      await must(back_app.user_set('flag.is_onboard', true))
      
      let flag_key = is_brand ? 'flag.is_brand' : 'flag.is_creator'
      await must(back_app.org_set(flag_key, true))
      window.location.reload()
    } catch(e: any) {
      err_set(e?.message || 'failed')
      submitting_set(false)
    }
  }

  let choice_step = (
    <div className="grid grid-cols-2 gap-4">
      {types.map(t=>(
        <button
          key={t}
          type="button"
          onClick={()=>{ user_type_set(t); step_set(1) }}
          className="py-8 px-6 rounded-xl font-bold text-lg bg-gray-100 text-gray-700
            hover:bg-gradient-to-r hover:from-[#0066FF] hover:to-[#6B4DE6]
            hover:text-white transition-all"
        >
          {t == 'creator' ? 'Creator' : 'Brand'}
        </button>
      ))}
    </div>
  )

  let form_step = (
    <div className="space-y-4 text-left">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={label_cls}>First name</label>
          <input className={field_cls} value={first}
            onChange={e=>first_set(e.target.value)} />
        </div>
        <div>
          <label className={label_cls}>Last/family name</label>
          <input className={field_cls} value={last}
            onChange={e=>last_set(e.target.value)} />
        </div>
      </div>
      {is_creator && (
        <div>
          <label className={label_cls}>Display name / Nickname</label>
          <input className={field_cls} value={nick}
            onChange={e=>nick_set(e.target.value)} />
        </div>
      )}
      <div>
        <label className={label_cls}>
          Company{is_creator ? ' (optional)' : ''}
        </label>
        <input className={field_cls} value={company}
          onChange={e=>company_set(e.target.value)} />
      </div>
      <div>
        <label className={label_cls}>
          Website{is_creator ? ' (optional)' : ''}
        </label>
        <input className={field_cls} placeholder="https://" value={website}
          onChange={e=>website_set(e.target.value)} />
      </div>
      {is_brand && (
        <div>
          <label className={label_cls}>Brand category</label>
          <select className={field_cls} value={category}
            onChange={e=>category_set(e.target.value)}>
            <option value="">Select a category</option>
            {cat_ids.map(id=>(
              <option key={id} value={id}>{cat_id2lbl(id)}</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className={label_cls}>Phone number</label>
        <input className={field_cls} placeholder="+18777755232" value={phone}
          onChange={e=>phone_set(e.target.value)} />
      </div>
      <label className="flex items-start gap-2 text-sm text-gray-600">
        <input type="checkbox" className="mt-1" checked={agree}
          onChange={e=>agree_set(e.target.checked)} />
        <span>
          I agree to the{' '}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
            className="text-primary-blue font-semibold hover:underline">
            Privacy Policy</a>,{' '}
          <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
            className="text-primary-blue font-semibold hover:underline">
            Terms of Service</a>{' '}and{' '}
          <a href="/legal/terms#disclaimer-of-warranties" target="_blank"
            rel="noopener noreferrer"
            className="text-primary-blue font-semibold hover:underline">
            Disclaimer</a>
        </span>
      </label>
      {err && <p className="text-red-600 text-sm">{err}</p>}
      <div className="grid grid-cols-2 gap-4 pt-2">
        <button
          type="button"
          onClick={()=>{ step_set(0); user_type_set(null); err_set(null) }}
          className="py-3 rounded-xl font-bold bg-gray-100 text-gray-700
            hover:bg-gray-200 transition-all"
        >
          Back
        </button>
        <button
          type="button"
          onClick={on_submit}
          disabled={submitting}
          className="py-3 rounded-xl font-bold text-white bg-gradient-to-r
            from-[#0066FF] to-[#6B4DE6] hover:shadow-xl transition-all
            disabled:opacity-60"
        >
          {submitting ? 'Saving...' : 'Submit'}
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-blue via-primary-purple to-primary-blue flex items-center justify-center px-4 py-8">
      <div className="max-w-lg w-full bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-8 text-center">
        <Org_select />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Just one more step before you start</h1>
        <p className="text-gray-600 mb-8">
          {step == 0
            ? "Tell us how you'll use Sponly. This is a one-time choice."
            : is_brand ? 'Tell us about your brand.' : 'Tell us about yourself.'}
        </p>
        {step == 0 ? choice_step : form_step}
        <button
          onClick={()=>logout({logoutParams: {returnTo: window.location.origin}})}
          className="mt-6 w-full text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}